import React, {useState} from 'react';
import {ConfigApi, OrderApi, OrderTrackingStatus, OrderType} from "../../../../services/Api/types";
import {Trans, useTranslation} from "react-i18next";
import {FormatAddressString} from "go-core/components/Formatters/FormatAddress";
import {
    countdown,
    DateHelper,
    getHourTimeString,
    parseDate,
    sameDateWithoutTime
} from "../../../../services/Common/Date/DateHelper";
import DateService from "../../../../services/Common/Date/DateService";
import SummaryCancelModal from "../SummaryCancelModal";

interface Props {
    orderTrackingStatus: OrderTrackingStatus;
    order: OrderApi;
    onUpdate: (order: OrderApi) => void;
    config: ConfigApi;
}

const OrderPreparationStatusInfo = ({orderTrackingStatus, order, config, onUpdate}: Props) => {
    const {t} = useTranslation();
    const orderNumber = order.pickup_number ?? order.id;
    const [showCancelModal, setShowCancelModal] = useState(false);
    const message = config.layout.custom_texts?.customer_success_info;

    const getFormattedTime = (date: string) => {
        if (sameDateWithoutTime(date, DateHelper.toString(new Date()))) {
            return getHourTimeString(DateHelper.fromString(date));
        } else {
            return DateHelper.format(DateHelper.fromString(date));
        }
    }

    const getDeliveryInfoTitle = () => {
        if (orderTrackingStatus === OrderTrackingStatus.RECEIVED && order.tracking.received_at) {
            return t("modules.summary.field.delivery_received_info.title", {
                address: FormatAddressString(order.address),
                time: getFormattedTime(order.tracking.received_at),
                orderNumber
            });
        }

        if (order.estimated_preparation_at) {
            return <Trans
                i18nKey="modules.summary.field.in_delivery.title"
                values={{
                    address: FormatAddressString(order.address),
                    time: countdown(DateHelper.fromString(order.estimated_preparation_at)),
                    orderNumber
                }}
                components={{
                    strong: <strong className="text-primary"/>
                }}
            />
        }

        return <Trans
            i18nKey="modules.summary.field.in_delivery_without_time.title"
            values={{
                address: FormatAddressString(order.address),
                orderNumber
            }}
            components={{
                strong: <strong className="text-primary"/>
            }}
        />
    };

    const getTakeawayInfoTitle = () => {
        if (orderTrackingStatus === OrderTrackingStatus.RECEIVED && order.tracking.received_at) {
            return t("modules.summary.field.takeaway_received_info.title", {
                time: getFormattedTime(order.tracking.received_at),
                orderNumber
            });
        }

        if (orderTrackingStatus === OrderTrackingStatus.READY) {
            return (
                <Trans
                    i18nKey="modules.summary.field.takeaway_ready_to_collect.title"
                    values={{
                        orderNumber
                    }}
                    components={{
                        strong: <strong className="text-success"/>
                    }}
                />
            )
        }

        if (order.estimated_preparation_at) {
            return <Trans
                i18nKey="modules.summary.field.takeaway_in_preparation.title"
                values={{
                    time: countdown(DateHelper.fromString(order.estimated_preparation_at)),
                    orderNumber
                }}
                components={{
                    strong: <strong className="text-primary"/>
                }}
            />
        }

        return <Trans
            i18nKey="modules.summary.field.takeaway_in_preparation_without_time.title"
            values={{
                orderNumber
            }}
            components={{
                strong: <strong className="text-primary"/>
            }}
        />
    }

    const getDineInInfoTitle = () => {
        if (orderTrackingStatus === OrderTrackingStatus.RECEIVED && order.tracking.received_at) {
            return t("modules.summary.field.dine_in_received_info.title", {
                table: order.number,
                time: getFormattedTime(order.tracking.received_at),
                orderNumber
            });
        }

        if (orderTrackingStatus === OrderTrackingStatus.READY) {
            return (
                <Trans
                    i18nKey="modules.summary.field.dine_in_ready.title"
                    values={{
                        orderNumber,
                        table: order.number,
                    }}
                    components={{
                        strong: <strong className="text-primary"/>
                    }}
                />
            )
        }

        if (order.estimated_preparation_at) {
            return <Trans
                i18nKey="modules.summary.field.dine_in_preparation.title"
                values={{
                    time: countdown(DateHelper.fromString(order.estimated_preparation_at)),
                    orderNumber,
                    table: order.number,
                }}
                components={{
                    strong: <strong className="text-primary"/>
                }}
            />
        }

        return <Trans
            i18nKey="modules.summary.field.dine_in_preparation_without_time.title"
            values={{
                orderNumber,
                table: order.number,
            }}
            components={{
                strong: <strong className="text-primary"/>
            }}
        />
    }

    const getRoomServiceInfoTitle = () => {
        if (orderTrackingStatus === OrderTrackingStatus.RECEIVED && order.tracking.received_at) {
            return t("modules.summary.field.room_service_received_info.title", {
                room: order.number,
                time: getFormattedTime(order.tracking.received_at),
                orderNumber
            });
        }

        if (orderTrackingStatus === OrderTrackingStatus.READY) {
            return (
                <Trans
                    i18nKey="modules.summary.field.room_service_ready.title"
                    values={{
                        orderNumber,
                        room: order.number,
                    }}
                    components={{
                        strong: <strong className="text-primary"/>
                    }}
                />
            )
        }

        if (order.estimated_preparation_at) {
            return <Trans
                i18nKey="modules.summary.field.room_service_in_preparation.title"
                values={{
                    time: countdown(DateHelper.fromString(order.estimated_preparation_at)),
                    orderNumber,
                    room: order.number,
                }}
                components={{
                    strong: <strong className="text-primary"/>
                }}
            />
        }

        return <Trans
            i18nKey="modules.summary.field.room_service_in_preparation_without_time.title"
            values={{
                orderNumber,
                room: order.number,
            }}
            components={{
                strong: <strong className="text-primary"/>
            }}
        />
    }

    const getTitle = () => {
        if (order.type === OrderType.DELIVERY) {
            return getDeliveryInfoTitle();
        }

        if ((order.type === OrderType.DINE_IN && !order.number) || order.type === OrderType.PICK_UP) {
            return getTakeawayInfoTitle();
        }

        if (order.type === OrderType.DINE_IN && order.number) {
            return getDineInInfoTitle();
        }

        if (order.type === OrderType.ROOM_SERVICE && order.number) {
            return getRoomServiceInfoTitle();
        }

        return ""
    }

    const isCancelPossible = () => {
        if (order.status === "ACCEPTED" || order.status === "CLOSED") return false;

        const cancellationTime = config?.ordering.cancellation_time;

        if (cancellationTime && cancellationTime > 0) {
            const confirmedAt = parseDate(order.confirmed_at);
            const now = DateService.now();
            const cancellationTimeInMilliseconds = cancellationTime * 60 * 1000;

            return confirmedAt > new Date(now.getTime() - cancellationTimeInMilliseconds)
        }

        return false;
    }

    const getAdditionalContainerClassName = () => {
        if ((order.type === OrderType.DINE_IN && !order.number) || order.type === OrderType.PICK_UP) {
            if (orderTrackingStatus === OrderTrackingStatus.READY) return 'background-success';

            return undefined;
        }

        return undefined;
    }

    return (
        <>
            {showCancelModal && (
                <SummaryCancelModal order={order} onHide={() => setShowCancelModal(false)}
                                    onSuccess={onUpdate}/>
            )}
            <div
                className={`summary-order-preparation-status-info-container ${getAdditionalContainerClassName() ?? ""}`}>
                <h1>{getTitle()}</h1>
                {message && <p className="mb-0 text-muted fw-normal message">{message}</p>}
                {isCancelPossible() && (
                    <button className="btn btn-default btn-lg" onClick={() => setShowCancelModal(true)}>
                        {t("common.action.cancel_order")}
                    </button>
                )}
            </div>
        </>
    )
}

export default OrderPreparationStatusInfo;